"use client";

import { useState } from "react";
import Link from "next/link";
import { CheckCircle2, Loader2, Lock, Sparkles } from "lucide-react";
import { createClient } from "@/lib/supabase/client";
import { withCvBuilderAnalyticsContext } from "@/components/cv-builder/cv-builder-analytics";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { trackEvent } from "@/lib/analytics";
import {
  OAUTH_NEXT_COOKIE_MAX_AGE_SECONDS,
  OAUTH_NEXT_COOKIE_NAME,
} from "@/lib/auth/oauth-constants";

interface BuilderAuthDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  locale: string;
  redirectTo: string;
  analyticsContext?: UmamiEventData;
}

export function BuilderAuthDialog({
  open,
  onOpenChange,
  locale,
  redirectTo,
  analyticsContext,
}: BuilderAuthDialogProps) {
  const [error, setError] = useState<string | null>(null);
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);

  const handleGoogleSignUp = async () => {
    const supabase = createClient();
    setIsGoogleLoading(true);
    setError(null);

    try {
      const secureCookie = window.location.protocol === "https:" ? "; secure" : "";
      document.cookie = `${OAUTH_NEXT_COOKIE_NAME}=${encodeURIComponent(redirectTo)}; path=/; max-age=${OAUTH_NEXT_COOKIE_MAX_AGE_SECONDS}; samesite=lax${secureCookie}`;

      trackEvent(
        "oauth_start",
        withCvBuilderAnalyticsContext(analyticsContext, {
          locale,
          source: "builder_download_gate",
        }),
      );

      const { error: oauthError } = await supabase.auth.signInWithOAuth({
        provider: "google",
        options: {
          redirectTo: `${window.location.origin}/auth/oauth?next=${encodeURIComponent(redirectTo)}`,
        },
      });

      if (oauthError) {
        throw oauthError;
      }
    } catch (unknownError: unknown) {
      setError(
        unknownError instanceof Error ? unknownError.message : "Terjadi kesalahan.",
      );
    } finally {
      setIsGoogleLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md rounded-2xl border border-border p-0 shadow-xl">
        <div className="space-y-6 p-6">
          <DialogHeader className="space-y-3 text-left">
            <span className="inline-flex h-11 w-11 items-center justify-center rounded-full bg-primary/10 text-primary">
              <Lock className="h-5 w-5" />
            </span>
            <div className="space-y-2">
              <DialogTitle className="text-2xl tracking-tight text-foreground">
                Simpan CV & download PDF
              </DialogTitle>
              <DialogDescription className="text-sm leading-6 text-muted-foreground">
                Buat akun gratis agar CV kamu tersimpan. Setelah itu PDF akan
                langsung diunduh, dan kamu bisa edit lagi kapan saja.
              </DialogDescription>
            </div>
          </DialogHeader>

          <div className="space-y-2.5 rounded-xl border border-border bg-muted/30 p-4 text-sm text-muted-foreground">
            <div className="flex items-start gap-2">
              <CheckCircle2 className="mt-0.5 h-4 w-4 text-primary" />
              <span>CV yang barusan kamu bikin tidak hilang.</span>
            </div>
            <div className="flex items-start gap-2">
              <CheckCircle2 className="mt-0.5 h-4 w-4 text-primary" />
              <span>PDF siap kirim akan langsung terunduh setelah login.</span>
            </div>
            <div className="flex items-start gap-2">
              <Sparkles className="mt-0.5 h-4 w-4 text-primary" />
              <span>Kamu bisa balik lagi untuk edit CV atau cek skor ATS.</span>
            </div>
          </div>

          <div className="space-y-3">
            <Button
              type="button"
              className="w-full"
              onClick={() => void handleGoogleSignUp()}
              disabled={isGoogleLoading}
            >
              {isGoogleLoading ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : null}
              {isGoogleLoading ? "Menghubungkan..." : "Lanjut dengan Google"}
            </Button>

            <p className="text-center text-xs text-muted-foreground">
              Gratis, tanpa syarat.
            </p>

            <p className="text-center text-xs text-muted-foreground">
              Butuh halaman daftar biasa?{" "}
              <Link
                href={`/${locale}/sign-up?redirect=${encodeURIComponent(redirectTo)}`}
                className="font-medium text-foreground underline underline-offset-4"
              >
                Buka halaman daftar
              </Link>
            </p>

            {error ? (
              <p className="text-center text-xs text-destructive">{error}</p>
            ) : null}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
